import React, { useState } from "react";
import { useTranslation } from 'react-i18next';
import { formatDateTime, formatDateTimeForCopy, formatDaysWithMinimum, timestampToDateString, dateStringToTimestamp } from "../utils/dateUtils";
import Modal from './ui/Modal';

function RecordList({
  overtimeRecords,
  restRecords,
  consumptionRecords,
  annualLeaveRecords,
  expandedRecord,
  onExpand,
  onCollapse,
  onDelete,
  onEdit,
  showEditModal,
  editRecord,
  setEditRecord,
  onEditSubmit,
  onEditCancel,
  onRestDelete,
  onRestEdit,
  onRestEditSubmit,
  onAnnualLeaveDelete,
  onAnnualLeaveEdit,
  onCleanRedundantRecords,
  showToast,
}) {
  const { t } = useTranslation();
  const [overtimePage, setOvertimePage] = useState(1);
  const [restPage, setRestPage] = useState(1);
  const [annualLeavePage, setAnnualLeavePage] = useState(1);
  const [overtimeRecordsPerPage, setOvertimeRecordsPerPage] = useState(5);
  const [restRecordsPerPage, setRestRecordsPerPage] = useState(5);
  const [annualLeaveRecordsPerPage, setAnnualLeaveRecordsPerPage] = useState(5);

  const paginateRecords = (records, page, recordsPerPage, sortKey = 'startTime') => {
    // 先按时间倒序排序（最新在前），然后分页
    const sortedRecords = [...records].sort((a, b) => {
      const timeA = a[sortKey] || a.startTime || a.txbegintime;
      const timeB = b[sortKey] || b.startTime || b.txbegintime;
      return new Date(timeB) - new Date(timeA);
    });
    const startIndex = (page - 1) * recordsPerPage;
    return sortedRecords.slice(startIndex, startIndex + recordsPerPage);
  };

  const overtimePageCount = Math.ceil(overtimeRecords.length / overtimeRecordsPerPage);
  const restPageCount = Math.ceil(restRecords.length / restRecordsPerPage);
  const annualLeavePageCount = Math.ceil(annualLeaveRecords.length / annualLeaveRecordsPerPage);

  const renderPagination = (currentPage, setPage, pageCount, recordsPerPage, setRecordsPerPage, label) => (
    <div className="pagination">
      <button
        className="big-button"
        onClick={() => setPage(Math.max(1, currentPage - 1))}
        disabled={currentPage === 1}
      >
        {t('pagination.previous', '上一页')}
      </button>
      <span className="page">
        {t('pagination.pageInfo', '第 {{current}} 页，共 {{total}} 页', { current: currentPage, total: pageCount })}
      </span>
      <button
        className="big-button"
        onClick={() => setPage(Math.min(pageCount, currentPage + 1))}
        disabled={currentPage === pageCount}
      >
        {t('pagination.next', '下一页')}
      </button>
      <select
        value={recordsPerPage}
        onChange={(e) => {
          setRecordsPerPage(Number(e.target.value));
          setPage(1);  // 重置到第一页
        }}
        className="records-per-page"
      >
        <option value={5}>{t('pagination.itemsPerPage', '{{count}}条/页', { count: 5 })}</option>
        <option value={10}>{t('pagination.itemsPerPage', '{{count}}条/页', { count: 10 })}</option>
        <option value={20}>{t('pagination.itemsPerPage', '{{count}}条/页', { count: 20 })}</option>
      </select>
    </div>
  );

  const handleCopy = (record) => {
    const relatedConsumptions = consumptionRecords.filter(
      c => c.restStartTime === record.txbegintime && c.restEndTime === record.txendtime
    );

    let overtimeText = '';
    let overtimeHours = 0;
    let annualLeaveText = '';
    let annualLeaveHours = 0;

    relatedConsumptions.forEach(consumption => {
      // 过滤掉0小时的消费记录
      if (consumption.deductionTime <= 0) {
        return;
      }

      if (consumption.type === '调休') {
        overtimeHours += consumption.deductionTime;
        const days = formatDaysWithMinimum(consumption.deductionTime);
        overtimeText += `${formatDateTimeForCopy(consumption.consumptionStartTime)} ${t('time.to', '至')} ${formatDateTimeForCopy(consumption.consumptionEndTime)} ${t('time.total', '共')} ${consumption.deductionTime.toFixed(2)} ${t('time.hours', '小时')}(${days}${t('time.days', '天')})\n`;
      } else if (consumption.type === '年假') {
        annualLeaveHours += consumption.deductionTime;
        const days = formatDaysWithMinimum(consumption.deductionTime);
        annualLeaveText += `${formatDateTimeForCopy(consumption.consumptionStartTime)} ${t('time.to', '至')} ${formatDateTimeForCopy(consumption.consumptionEndTime)} ${t('time.total', '共')} ${consumption.deductionTime.toFixed(2)} ${t('time.hours', '小时')}(${days}${t('time.days', '天')})\n`;
      }
    });

    let copyText = '';

    if (overtimeHours > 0) {
      copyText += `${t('copy.deductOvertimeTotal', '加班调休,扣除对应加班时间共（{{hours}}小时）', { hours: overtimeHours.toFixed(2) })}\n${overtimeText}`;
    }

    if (annualLeaveHours > 0) {
      if (overtimeHours > 0) {
        copyText += '\n'; // 如果有加班时间，则在年假部分之前添加一个空行
      }
      copyText += `${t('copy.deductAnnualLeaveTotal', '扣除年假时间共（{{hours}}小时）', { hours: annualLeaveHours.toFixed(2) })}\n${annualLeaveText}`;
    }

    navigator.clipboard.writeText(copyText).then(() => {
      showToast(t('messages.copiedToClipboard', '已复制到剪贴板'));
    }, (err) => {
      console.error(t('copy.errorPrefix', '无法复制文本: '), err);
      showToast(t('messages.copyFailed', '复制失败，请重试'));
    });
  };

  const handleAnnualLeaveCopy = (record) => {
    const startTime = formatDateTimeForCopy(record.startTime);
    const endTime = formatDateTimeForCopy(record.endTime);
    const hours = record.duration.toFixed(2);
    const days = formatDaysWithMinimum(record.duration);

    const copyText = `${t('copy.annualLeaveTitle', '年假休假:')}\n${startTime} ${t('time.to', '至')} ${endTime} ${t('time.total', '共')} ${hours}${t('time.hours', '小时')}(${days}${t('time.days', '天')})`;

    navigator.clipboard.writeText(copyText).then(() => {
      showToast(t('messages.copiedToClipboard', '已复制到剪贴板'));
    }, (err) => {
      console.error(t('copy.errorPrefix', '无法复制文本: '), err);
      showToast(t('messages.copyFailed', '复制失败，请重试'));
    });
  };

  return (
    <div>
      <h3>{t('records.overtimeRecords', '牛马记录')}</h3>
      {/* <div className="record-actions">
        <button className="big-button" onClick={onCleanRedundantRecords}>
          清理冗余记录
        </button>
      </div> */}
      <table>
        <thead>
          <tr>
            <th>{t('table.details', '详细记录')}</th>
            <th>{t('table.number', '序号')}</th>
            <th>{t('table.type', '类型')}</th>
            <th>{t('table.startTime', '开始时间')}</th>
            <th>{t('table.endTime', '结束时间')}</th>
            <th>{t('table.duration', '总时长')}</th>
            <th>{t('table.remainingTime', '剩余可摆烂时间')}</th>
            <th>{t('table.actions', '操作')}</th>
          </tr>
        </thead>
        <tbody>
          {paginateRecords(overtimeRecords, overtimePage, overtimeRecordsPerPage, 'startTime').map(
            (record, index) => (
              <React.Fragment key={record.id}>
                <tr className={expandedRecord === record ? "expanded" : ""}>
                  <td>
                    {expandedRecord === record ? (
                      <button className="big-button" onClick={onCollapse}>
                        <svg
                          width="24"
                          height="24"
                          viewBox="0 0 48 48"
                          fill="none"
                          xmlns="http://www.w3.org/2000/svg"
                        >
                          <path
                            d="M36 18L24 30L12 18"
                            stroke="#000000"
                            strokeWidth="4"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                          />
                        </svg>
                      </button>
                    ) : (
                      <button
                        className="big-button"
                        onClick={() => onExpand(record)}
                      >
                        <svg
                          width="24"
                          height="24"
                          viewBox="0 0 48 48"
                          fill="none"
                          xmlns="http://www.w3.org/2000/svg"
                        >
                          <path
                            d="M19 12L31 24L19 36"
                            stroke="#000000"
                            strokeWidth="4"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                          />
                        </svg>
                      </button>
                    )}
                  </td>
                  <td>{(overtimePage - 1) * overtimeRecordsPerPage + index + 1}</td>
                  <td>
                    <span className="tag annual-leave">{t('records.overtime', '牛马')}</span>
                  </td>
                  <td>{formatDateTime(record.startTime)}</td>
                  <td>{formatDateTime(record.endTime)}</td>
                  <td>{record.duration.toFixed(2)} {t('time.hours', '小时')}</td>
                  <td>{record.remainingTime.toFixed(2)} {t('time.hours', '小时')}</td>
                  <td>
                    <button
                      className="delete-button"
                      onClick={() => onDelete(record)}
                    >
                      <svg
                        width="24"
                        height="24"
                        viewBox="0 0 48 48"
                        fill="none"
                        xmlns="http://www.w3.org/2000/svg"
                      >
                        <path
                          d="M9 10V44H39V10H9Z"
                          fill="none"
                          stroke="#000000"
                          strokeWidth="4"
                          strokeLinejoin="round"
                        />
                        <path
                          d="M20 20V33"
                          stroke="#000000"
                          strokeWidth="4"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        />
                        <path
                          d="M28 20V33"
                          stroke="#000000"
                          strokeWidth="4"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        />
                        <path
                          d="M4 10H44"
                          stroke="#000000"
                          strokeWidth="4"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        />
                        <path
                          d="M16 10L19.289 4H28.7771L32 10H16Z"
                          fill="none"
                          stroke="#000000"
                          strokeWidth="4"
                          strokeLinejoin="round"
                        />
                      </svg>
                    </button>
                    <p></p>
                    <button
                      className="big-button"
                      onClick={() => onEdit(record)}
                    >
                      <svg
                        width="24"
                        height="24"
                        viewBox="0 0 48 48"
                        fill="none"
                        xmlns="http://www.w3.org/2000/svg"
                      >
                        <path
                          d="M7 42H43"
                          stroke="#000000"
                          strokeWidth="4"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        />
                        <path
                          d="M11 26.7199V34H18.3172L39 13.3081L31.6951 6L11 26.7199Z"
                          fill="none"
                          stroke="#000000"
                          strokeWidth="4"
                          strokeLinejoin="round"
                        />
                      </svg>
                    </button>
                  </td>
                </tr>
                {expandedRecord === record && (
                  <tr className="rest-consumption">
                    <td colSpan="8">
                      <h4>{t('table.consumptionRecords', '摆烂消耗记录')}</h4>
                      <table>
                        <thead>
                          <tr>
                            <th>{t('table.number', '序号')}</th>
                            <th>{t('table.type', '类型')}</th>
                            <th>{t('table.restRequestStartTime', '摆烂申请开始时间')}</th>
                            <th>{t('table.restRequestEndTime', '摆烂申请结束时间')}</th>
                            <th>{t('table.actualConsumptionStartTime', '实际消耗开始时间')}</th>
                            <th>{t('table.actualConsumptionEndTime', '实际消耗结束时间')}</th>
                            <th>{t('table.deductionTime', '摆烂扣除时间')}</th>
                          </tr>
                        </thead>
                        <tbody>
                          {/* 在 consumptionRecords 映射之前，按照 consumptionStartTime 排序 */}
                          {consumptionRecords
                            .filter(consumption => consumption.overtimeId === record.id)
                            .sort((a, b) => a.consumptionStartTime - b.consumptionStartTime)
                            .map((consumption, idx) => (
                              <tr key={consumption.id}>
                                <td>{idx + 1}</td>
                                <td>
                                  <span
                                    className={`tag ${
                                      consumption.type === t('records.annualLeaveType', '年假')
                                        ? "annual-leave"
                                        : "comp-leave"
                                    }`}
                                  >
                                    {consumption.type === t('records.annualLeaveType', '年假')
                                      ? t('records.annualLeave', '年假')
                                      : t('records.rest', '摆烂')}
                                  </span>
                                </td>
                                <td>
                                  {formatDateTime(consumption.restStartTime)}
                                </td>
                                <td>
                                  {formatDateTime(consumption.restEndTime)}
                                </td>
                                <td>
                                  {formatDateTime(
                                    consumption.consumptionStartTime
                                  )}
                                </td>
                                <td>
                                  {formatDateTime(
                                    consumption.consumptionEndTime
                                  )}
                                </td>
                                <td>
                                  {consumption.deductionTime.toFixed(2)} {t('time.hours', '小时')}
                                </td>
                              </tr>
                            ))}
                        </tbody>
                      </table>
                    </td>
                  </tr>
                )}
              </React.Fragment>
            )
          )}
        </tbody>
      </table>
      {renderPagination(overtimePage, setOvertimePage, overtimePageCount, overtimeRecordsPerPage, setOvertimeRecordsPerPage, t('records.overtimeRecords', '加班记录'))}

      <h3>{t('records.restRecords', '摆烂记录')}</h3>
      <table>
        <thead>
          <tr>
            <th>{t('table.number', '序号')}</th>
            <th>{t('table.type', '类型')}</th>
            <th>{t('table.restRequestStartTime', '申请摆烂开始时间')}</th>
            <th>{t('table.restRequestEndTime', '申请摆烂结束时间')}</th>
            <th>{t('table.duration', '总时长')}</th>
            <th>{t('table.actions', '操作')}</th>
          </tr>
        </thead>
        <tbody>
          {paginateRecords(restRecords, restPage, restRecordsPerPage, 'txbegintime').map((record, index) => (
            <tr key={record.id}>
              <td>{(restPage - 1) * restRecordsPerPage + index + 1}</td>
              <td>
                <span className="tag comp-leave">{t('records.rest', '摆烂')}</span>
              </td>
              <td>{formatDateTime(record.txbegintime)}</td>
              <td>{formatDateTime(record.txendtime)}</td>
              <td>{record.duration.toFixed(2)} {t('time.hours', '小时')}</td>
              <td>
                <button
                  className="delete-button"
                  onClick={() => onRestDelete(record)}
                >
                  <svg
                    width="24"
                    height="24"
                    viewBox="0 0 48 48"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <path
                      d="M9 10V44H39V10H9Z"
                      fill="none"
                      stroke="#000000"
                      strokeWidth="4"
                      strokeLinejoin="round"
                    />
                    <path
                      d="M20 20V33"
                      stroke="#000000"
                      strokeWidth="4"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                    <path
                      d="M28 20V33"
                      stroke="#000000"
                      strokeWidth="4"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                    <path
                      d="M4 10H44"
                      stroke="#000000"
                      strokeWidth="4"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                    <path
                      d="M16 10L19.289 4H28.7771L32 10H16Z"
                      fill="none"
                      stroke="#000000"
                      strokeWidth="4"
                      strokeLinejoin="round"
                    />
                  </svg>
                </button>
                <button
                  className="big-button"
                  onClick={() => onRestEdit(record)}
                >
                  <svg
                    width="24"
                    height="24"
                    viewBox="0 0 48 48"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <path
                      d="M7 42H43"
                      stroke="#000000"
                      strokeWidth="4"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                    <path
                      d="M11 26.7199V34H18.3172L39 13.3081L31.6951 6L11 26.7199Z"
                      fill="none"
                      stroke="#000000"
                      strokeWidth="4"
                      strokeLinejoin="round"
                    />
                  </svg>
                </button>
                <button
                  className="big-button"
                  onClick={() => handleCopy(record)}
                >
                  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M20 9H11C9.89543 9 9 9.89543 9 11V20C9 21.1046 9.89543 22 11 22H20C21.1046 22 22 21.1046 22 20V11C22 9.89543 21.1046 9 20 9Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                    <path d="M5 15H4C3.46957 15 2.96086 14.7893 2.58579 14.4142C2.21071 14.0391 2 13.5304 2 13V4C2 3.46957 2.21071 2.96086 2.58579 2.58579C2.96086 2.21071 3.46957 2 4 2H13C13.5304 2 14.0391 2.21071 14.4142 2.58579C14.7893 2.96086 15 3.46957 15 4V5" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                  </svg>
                </button>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
      {renderPagination(restPage, setRestPage, restPageCount, restRecordsPerPage, setRestRecordsPerPage, t('records.restRecords', '摆烂记录'))}

      <h3>{t('records.annualLeaveRecords', '年假记录')}</h3>
      <table>
        <thead>
          <tr>
            <th>{t('table.number', '序号')}</th>
            <th>{t('table.type', '类型')}</th>
            <th>{t('table.annualLeaveStartTime', '年假开始时间')}</th>
            <th>{t('table.annualLeaveEndTime', '年假结束时间')}</th>
            <th>{t('table.duration', '总时长')}</th>
            <th>{t('table.actions', '操作')}</th>
          </tr>
        </thead>
        <tbody>
          {paginateRecords(annualLeaveRecords, annualLeavePage, annualLeaveRecordsPerPage, 'startTime').map(
            (record, index) => (
              <tr key={record.id}>
                <td>{(annualLeavePage - 1) * annualLeaveRecordsPerPage + index + 1}</td>
                <td>
                  <span className="tag annual-leave">{t('records.annualLeave', '年假')}</span>
                </td>
                <td>{formatDateTime(record.startTime)}</td>
                <td>{formatDateTime(record.endTime)}</td>
                <td>{record.duration.toFixed(2)} {t('time.hours', '小时')}</td>
                <td>
                  <button
                    className="delete-button"
                    onClick={() => onAnnualLeaveDelete(record)}
                  >
                    <svg
                      width="24"
                      height="24"
                      viewBox="0 0 48 48"
                      fill="none"
                      xmlns="http://www.w3.org/2000/svg"
                    >
                      <path
                        d="M9 10V44H39V10H9Z"
                        fill="none"
                        stroke="#000000"
                        strokeWidth="4"
                        strokeLinejoin="round"
                      />
                      <path
                        d="M20 20V33"
                        stroke="#000000"
                        strokeWidth="4"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                      <path
                        d="M28 20V33"
                        stroke="#000000"
                        strokeWidth="4"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                      <path
                        d="M4 10H44"
                        stroke="#000000"
                        strokeWidth="4"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                      <path
                        d="M16 10L19.289 4H28.7771L32 10H16Z"
                        fill="none"
                        stroke="#000000"
                        strokeWidth="4"
                        strokeLinejoin="round"
                      />
                    </svg>
                  </button>
                  <button
                    className="big-button"
                    onClick={() => handleAnnualLeaveCopy(record)}
                  >
                    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                      <path d="M20 9H11C9.89543 9 9 9.89543 9 11V20C9 21.1046 9.89543 22 11 22H20C21.1046 22 22 21.1046 22 20V11C22 9.89543 21.1046 9 20 9Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                      <path d="M5 15H4C3.46957 15 2.96086 14.7893 2.58579 14.4142C2.21071 14.0391 2 13.5304 2 13V4C2 3.46957 2.21071 2.96086 2.58579 2.58579C2.96086 2.21071 3.46957 2 4 2H13C13.5304 2 14.0391 2.21071 14.4142 2.58579C14.7893 2.96086 15 3.46957 15 4V5" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                    </svg>
                  </button>
                </td>
              </tr>
            )
          )}
        </tbody>
      </table>
      {renderPagination(annualLeavePage, setAnnualLeavePage, annualLeavePageCount, annualLeaveRecordsPerPage, setAnnualLeaveRecordsPerPage, t('records.annualLeaveRecords', '年假记录'))}

      <Modal
        open={showEditModal}
        onClose={onEditCancel}
        title={
          editRecord?.txbegintime
            ? t('modals.editRestTitle', '修改摆烂记录')
            : editRecord?.type === "年假"
            ? t('modals.editAnnualLeaveTitle', '修改年假记录')
            : t('modals.editOvertimeTitle', '修改牛马记录')
        }
        size="md"
      >
        <form
          onSubmit={(e) => {
            e.preventDefault();
            onEditSubmit(editRecord);
          }}
          className="edit-form"
        >
          <div className="form-group">
            <label>
              {editRecord?.txbegintime
                ? t('time.startTime', '开始时间:')
                : editRecord?.type === "年假"
                ? t('forms.annualLeaveStartTime', '年假开始时间:')
                : t('forms.overtimeStartTime', '牛马开始时间:')}
            </label>
            <input
              type="datetime-local"
              value={timestampToDateString(
                editRecord?.txbegintime || editRecord?.startTime
              )}
              onChange={(e) =>
                setEditRecord({
                  ...editRecord,
                  [editRecord?.txbegintime ? "txbegintime" : "startTime"]:
                    dateStringToTimestamp(e.target.value),
                })
              }
              required
            />
          </div>

          <div className="form-group">
            <label>
              {editRecord?.txendtime
                ? t('time.endTime', '结束时间:')
                : editRecord?.type === "年假"
                ? t('forms.annualLeaveEndTime', '年假结束时间:')
                : t('forms.overtimeEndTime', '牛马结束时间:')}
            </label>
            <input
              type="datetime-local"
              value={timestampToDateString(
                editRecord?.txendtime || editRecord?.endTime
              )}
              onChange={(e) =>
                setEditRecord({
                  ...editRecord,
                  [editRecord?.txendtime ? "txendtime" : "endTime"]:
                    dateStringToTimestamp(e.target.value),
                })
              }
              required
            />
          </div>

          <div className="modal-actions">
            <button
              className="cancel-btn"
              type="button"
              onClick={onEditCancel}
            >
              {t('common.cancel', '取消')}
            </button>
            <button className="submit-btn" type="submit">
              {t('common.save', '保存')}
            </button>
          </div>
        </form>
      </Modal>
    </div>
  );
}

export default RecordList;